import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Megaphone,
  Mail,
  Users,
  Contact,
  Workflow,
  LayoutDashboard,
  UserPlus,
  Target,
  Handshake,
} from 'lucide-react'

import { AnimatedTabs, type AnimatedTab } from '@/components/customers/animated-tabs'
import { OverviewTab } from '@/features/marketing/components/overview-tab'
import { LeadsTab } from '@/features/marketing/components/leads-tab'
import { SourcesTab } from '@/features/marketing/components/sources-tab'
import { ReferralsTab } from '@/features/marketing/components/referrals-tab'
import { CampaignsTab } from '@/features/marketing/components/campaigns-tab'
import { TemplatesTab } from '@/features/marketing/components/templates-tab'
import { SegmentsTab } from '@/features/marketing/components/segments-tab'
import { AudienceTab } from '@/features/marketing/components/audience-tab'
import { AutomationTab } from '@/features/marketing/components/automation-tab'
import { requireFlow } from '@/features/auth/guards'

const MARKETING_TABS = [
  'overview',
  'leads',
  'sources',
  'referrals',
  'campaigns',
  'templates',
  'segments',
  'audience',
  'automation',
] as const
type MarketingTab = (typeof MARKETING_TABS)[number]

function isMarketingTab(value: unknown): value is MarketingTab {
  return typeof value === 'string' && (MARKETING_TABS as readonly string[]).includes(value)
}

export const Route = createFileRoute('/_authenticated/marketing/')({
  beforeLoad: requireFlow('marketing'),
  validateSearch: (search: Record<string, unknown>): { tab?: MarketingTab } => ({
    tab: isMarketingTab(search.tab) ? search.tab : undefined,
  }),
  component: MarketingPage,
})

function MarketingPage() {
  const { tab } = Route.useSearch()
  const navigate = useNavigate({ from: '/marketing/' })
  const activeTab: MarketingTab = tab ?? 'overview'

  const setActiveTab = (next: string) => {
    if (!isMarketingTab(next)) return
    navigate({ search: () => (next === 'overview' ? {} : { tab: next }) })
  }

  const tabs: AnimatedTab[] = [
    {
      value: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
      content: <OverviewTab />,
    },
    {
      value: 'leads',
      label: 'Leads',
      icon: UserPlus,
      content: <LeadsTab />,
    },
    {
      value: 'sources',
      label: 'Sources',
      icon: Target,
      content: <SourcesTab />,
    },
    {
      value: 'referrals',
      label: 'Referrals',
      icon: Handshake,
      content: <ReferralsTab />,
    },
    {
      value: 'campaigns',
      label: 'Campaigns',
      icon: Megaphone,
      content: <CampaignsTab />,
    },
    {
      value: 'templates',
      label: 'Templates',
      icon: Mail,
      content: <TemplatesTab />,
    },
    {
      value: 'segments',
      label: 'Segments',
      icon: Users,
      content: <SegmentsTab />,
    },
    {
      value: 'audience',
      label: 'Audience',
      icon: Contact,
      content: <AudienceTab />,
    },
    {
      value: 'automation',
      label: 'Automation',
      icon: Workflow,
      content: <AutomationTab />,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <Megaphone className="h-8 w-8" /> Marketing
        </h1>
        <p className="text-muted-foreground">
          Capture leads from every channel, see which sources bring in first-time customers, track
          referrals, and run email and SMS campaigns.
        </p>
      </div>

      <AnimatedTabs tabs={tabs} value={activeTab} onValueChange={setActiveTab} />
    </div>
  )
}
